import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MessageCircle, Mail, MapPin } from 'lucide-react';
import StepperForm from '../components/StepperForm';
import { TID } from '../lib/testIds';
import { BRAND } from '../lib/data';

const WHY = [
  { t: 'Local team on the ground', d: 'Based in Karbala — same person from first message to last farewell.' },
  { t: 'Hand-picked hotel partners', d: 'Walking distance from the Holy Shrine, vetted by us.' },
  { t: 'All-inclusive · No surprises', d: 'Visa, transfers, hotel, taxes — one quote.' },
];

export default function Home() {
  const waNumber = BRAND.whatsapp.replace(/\D/g, '');

  return (
    <div id="inquiry" data-testid={TID.inquirySection} className="relative">
      <div className="grid lg:grid-cols-12 min-h-screen">
        {/* LEFT — Cinematic brand panel (sticky on lg+) */}
        <aside
          data-testid={TID.heroSection}
          className="relative lg:col-span-5 lg:sticky lg:top-0 lg:h-screen overflow-hidden bg-emerald-deep text-[color:var(--gold-soft)]"
        >
          <div className="absolute inset-0">
            <img
              src="https://images.unsplash.com/photo-1564287535259-91d01d9a634b?auto=format&fit=crop&w=1600&q=85"
              alt="The golden dome of the Imam Hussain Shrine in Karbala"
              className="w-full h-full object-cover opacity-40"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-deep/85 via-emerald-deep/70 to-[#011b15]/95" />
          </div>
          <div className="absolute inset-0 grain opacity-30" aria-hidden="true" />

          <div className="relative h-full flex flex-col px-8 md:px-12 lg:px-14 py-12 lg:py-16 lg:overflow-y-auto">
            <Link to="/" className="inline-flex items-center gap-3 self-start">
              <div className="w-10 h-10 border border-[color:var(--gold)] flex items-center justify-center">
                <span className="font-serif text-xl leading-none text-[color:var(--gold)]">ز</span>
              </div>
              <div className="leading-tight">
                <div className="font-serif text-2xl text-white whitespace-nowrap">BookMyZiyarah</div>
                <div className="text-[10px] tracking-[0.28em] uppercase text-[color:var(--gold)]">Ziarat Concierge · Iraq</div>
              </div>
            </Link>

            <div className="mt-12 lg:mt-16 flex-1">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                className="eyebrow text-[color:var(--gold)] mb-5"
              >
                Karbala · Najaf · Kadhimiya · Samarra
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1.1, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="font-serif text-white text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-[-0.015em]"
              >
                The complete <em className="italic text-[color:var(--gold)]">Ziarat</em>,<br />
                planned in <em className="italic text-[color:var(--gold)]">one</em> message.
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1.0, delay: 0.3 }}
                className="mt-7 text-[color:var(--gold-soft)]/85 text-base md:text-lg leading-relaxed font-light max-w-md"
              >
                Hand-picked hotels in Karbala. Iraq visa concierge. Private transport across the four holy cities. Share your details &mdash; our concierge replies within the hour.
              </motion.p>

              <motion.ul
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 0.5 }}
                className="mt-12 space-y-5"
              >
                {WHY.map((w, i) => (
                  <li key={w.t} className="flex items-start gap-5">
                    <div className="font-serif text-2xl text-[color:var(--gold)] leading-none w-8 flex-shrink-0 pt-1">0{i + 1}</div>
                    <div>
                      <div className="font-serif text-lg md:text-xl text-white leading-tight">{w.t}</div>
                      <div className="text-sm text-[color:var(--gold-soft)]/70 mt-1">{w.d}</div>
                    </div>
                  </li>
                ))}
              </motion.ul>
            </div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.8 }}
              className="mt-12 pt-8 border-t border-[color:var(--gold)]/20 space-y-3 text-sm text-[color:var(--gold-soft)]/85"
            >
              <a href={`https://wa.me/${waNumber}?text=${encodeURIComponent('Salam, I would like to plan a Ziarat.')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 hover:text-white transition-colors">
                <MessageCircle size={16} strokeWidth={1.5} className="text-[color:var(--gold)]" /> {BRAND.whatsappPretty}
              </a>
              <a href={`mailto:${BRAND.email}`} className="flex items-center gap-3 hover:text-white transition-colors">
                <Mail size={16} strokeWidth={1.5} className="text-[color:var(--gold)]" /> {BRAND.email}
              </a>
              <div className="flex items-center gap-3">
                <MapPin size={16} strokeWidth={1.5} className="text-[color:var(--gold)]" /> {BRAND.address}
              </div>
              <div className="pt-4 font-serif text-base text-[color:var(--gold)] italic">بسم الله الرحمن الرحيم</div>
            </motion.div>
          </div>
        </aside>

        {/* RIGHT — Stepper form */}
        <div className="lg:col-span-7 bg-paper">
          <StepperForm />
        </div>
      </div>
    </div>
  );
}
